import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import {
  Card,
  Flex,
  Grid,
  Button,
  Divider,
  Heading3,
  Paragraph,
  InfoSection,
  PageWrapper,
  OneThirdLayout,
  TwoThirdsLayout,
  EmptyStatePlaceholder
} from '@ubisend/pulse-components';
import {
  useAuth,
  FEATURE_GATES,
  PermissionFilter,
  hasActiveFeatureGate
} from '@ubisend/pulse-auth';
import Icon from '@ubisend/pulse-icons';

import {
  Prompt,
  Provider
} from '../Components';

const ResourceCard = ({ name, description, link, icon, iconType }) => (
  <Card flex col tall between>
    <Flex col ySpaceSm>
      <Flex between center>
        <Icon
          size="1.5rem"
          height="1.5rem"
          width="1.5rem"
          type={icon}
          outline={(iconType === 'outline').toString()}
          solid={(iconType === 'solid').toString()}
        />
      </Flex>
      <Heading3>{name}</Heading3>
      <Flex grow>
        <Paragraph sm secondary>{description}</Paragraph>
      </Flex>
    </Flex>
    <Flex col shiftLeft shiftRight shiftDown>
      <Divider mbNone />
      <Flex center between>
        <Button
          fat="true"
          middle="true"
          as={Link}
          pad="true"
          to={link}
          variant="inline"
        >
          Manage
        </Button>
      </Flex>
    </Flex>
  </Card>
);

ResourceCard.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  iconType: PropTypes.oneOf(['outline', 'solid']).isRequired
};

const GenerativeAi = () => {
  const auth = useAuth();
  const { hasPermission } = useAuth();

  const canViewPrompt = hasPermission('view generative ai prompt');
  const canViewResources = hasPermission('view generative ai resource');

  const noPermission = !canViewPrompt && !canViewResources;

  const visibleResources = [
    {
      name: 'Website',
      description: 'Add website URLs or Sitemaps.',
      link: 'generative-ai/sitemap',
      icon: 'website',
      iconType: 'outline',
      show: true
    },
    {
      name: 'Document',
      description: 'Upload documents.',
      link: 'generative-ai/document',
      icon: 'document',
      iconType: 'outline',
      show: true
    },
    {
      name: 'Zendesk',
      description: 'Connect to Zendesk Helpcenter',
      link: 'generative-ai/zendesk',
      icon: 'zendesk',
      iconType: 'solid',
      show: hasActiveFeatureGate(FEATURE_GATES.GENERATIVE_AI_ZENDESK)(auth)
    },
    {
      name: 'SharePoint',
      description: 'Connect to SharePoint.',
      link: 'generative-ai/sharepoint',
      icon: 'sharepoint',
      iconType: 'outline',
      show: hasActiveFeatureGate(FEATURE_GATES.GENERATIVE_AI_SHAREPOINT)(auth)
    }
  ].filter(item => item.show);

  const showPromptSection = canViewPrompt;
  const showResourceSection = canViewResources && visibleResources.length > 0;
  const showProviderSection = false; // Currently disabled

  return (
    <PageWrapper beta="true" header="Generative AI" subheader="Configure your generative AI">
      <Flex col>
        {noPermission && (
          <EmptyStatePlaceholder
            type="knowledgebase"
            heading="Nothing to show yet"
            text="You do not have the permission to view the details. Please contact your administrator or the setup."
          />
        )}

        {/* Prompt Section */}
        {showPromptSection && (
          <PermissionFilter can="view generative ai prompt">
            <Flex mb>
              <OneThirdLayout>
                <InfoSection title="Prompt" />
              </OneThirdLayout>
              <TwoThirdsLayout>
                <Prompt />
              </TwoThirdsLayout>
            </Flex>
          </PermissionFilter>
        )}

        {/* Knowledge Sources Section */}
        {showResourceSection && (
          <>
            {showPromptSection && <Divider />}
            <Flex mb mt={showPromptSection ? true : undefined}>
              <OneThirdLayout>
                <InfoSection title="Knowledge Sources" />
              </OneThirdLayout>
              <TwoThirdsLayout>
                <Grid columns={3}>
                  {visibleResources.map(item => (
                    <ResourceCard key={item.name} {...item} />
                  ))}
                </Grid>
              </TwoThirdsLayout>
            </Flex>
          </>
        )}

        {/* Provider Section */}
        {showProviderSection && (
          <PermissionFilter can="view generative ai provider">
            {(showPromptSection || showResourceSection) && <Divider />}
            <Flex mb mt={(showPromptSection || showResourceSection) ? true : undefined}>
              <OneThirdLayout>
                <InfoSection title="Providers" />
              </OneThirdLayout>
              <TwoThirdsLayout>
                <Provider />
              </TwoThirdsLayout>
            </Flex>
          </PermissionFilter>
        )}
      </Flex>
    </PageWrapper>
  );
};

export default GenerativeAi;